import { Delete } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';
import { Patient } from 'api/models';
import { SaveButton } from 'components/SaveButton';
import { useFormik } from 'formik';
import { getPatientInitialValues, getPatientValidationSchema } from 'forms';
import { mutation } from 'operations';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  projectState,
  selectedPatientState,
  toastStateSelector,
} from 'state';
import { ModelTypes } from 'types/types';
import {
  getIsDeadValue,
  getIsPatientProjectIdDisabled,
  getPatientBody,
} from './utils';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import IdInput from 'components/IdInput/IdInput';

const getLabel = (
  requiredPatientFields: string[] = [],
  key: string,
  label: string,
) => {
  const required = requiredPatientFields?.includes(key) ? '*' : '';
  return `${label} ${required}`;
};

interface PatientEditorFormProps {
  id: string | undefined;
  patient: Patient | undefined;
  patientProjectId?: string;
  mode: 'view' | 'edit';
}

export default function PatientEditorForm({
  id,
  patient,
  patientProjectId,
  mode,
}: PatientEditorFormProps) {
  const setToast = useSetRecoilState(toastStateSelector);
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedProject } = useRecoilValue(projectState);
  const isReadOnly = mode === 'view';
  const setPatient = useSetRecoilState(selectedPatientState);

  const defaults = getPatientInitialValues(
    patient,
    searchParams,
    patientProjectId,
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: defaults,
    validationSchema: getPatientValidationSchema(
      t,
      selectedProject?.requiredPatientFields,
    ),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const body = getPatientBody(values, id ?? '');
        const { data: updatedPatient } = await mutation<Patient>({
          type: ModelTypes.patient,
          body,
          ids: {
            projectId: selectedProject?.id,
            id,
          },
        });
        setPatient(updatedPatient!);
        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
        setSubmitting(false);
        navigate(-1);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
        setSubmitting(false);
      }
    },
  });

  return (
    <Paper
      sx={{
        padding: theme => theme.spacing(3),
        mt: theme => theme.spacing(3),
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* Line 2 */}
          {formik.values.identifiers?.map((identifier, index) => (
            <Grid
              xs={12}
              md={12}
              item
              display="flex"
              flexDirection="row"
              key={`patient-form-id-field-key-${identifier.type}`}
            >
              <IdInput
                inputProps={{
                  readOnly: isReadOnly,
                }}
                value={identifier}
                onChange={({ id, type }) => {
                  formik.setFieldValue(`identifiers[${index}]`, {
                    type,
                    id,
                  });
                }}
                // @ts-ignore
                error={Boolean(formik.errors.identifiers?.[index]?.id)}
                // @ts-ignore
                helperText={formik.errors.identifiers?.[index]?.id}
              />
              {index !== 0 && (
                <IconButton
                  disabled={isReadOnly}
                  sx={{ ml: theme => theme.spacing(2), width: '65px' }}
                  color="secondary"
                  size="small"
                  type="button"
                  onClick={() => {
                    formik.handleChange({
                      target: {
                        name: 'identifiers',
                        value: formik.values.identifiers?.filter(
                          (_, i) => i !== index,
                        ),
                      },
                    });
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Grid>
          ))}
          <Grid xs={12} md={12} item justifyContent="flex-end" display="flex">
            <Button
              disabled={isReadOnly}
              color="secondary"
              variant="contained"
              type="button"
              onClick={() => {
                formik.handleChange({
                  target: {
                    name: 'identifiers',
                    value: [
                      ...(formik.values.identifiers ?? []),
                      { id: '', type: '' },
                    ],
                  },
                });
              }}
            >
              + {t('patientCreation.addPatientId')}
            </Button>
          </Grid>
          <Grid xs={12} md={12} item>
            <TextField
              fullWidth
              label={`${t('patientCreation.patientProjectId')} *`}
              type="text"
              name="patientProjectId"
              value={formik.values.patientProjectId}
              InputProps={{
                readOnly: isReadOnly,
              }}
              disabled={getIsPatientProjectIdDisabled(
                isReadOnly,
                selectedProject?.configuration?.editableProjectId,
              )}
              onChange={formik.handleChange}
              error={
                formik.touched.patientProjectId &&
                Boolean(formik.errors.patientProjectId)
              }
              helperText={
                formik.touched.patientProjectId &&
                formik.errors.patientProjectId
              }
            />
          </Grid>

          {/* Line 3 */}
          <Grid xs={12} md={4} item alignItems={'center'} display={'flex'}>
            <DatePicker
              disableFuture={true}
              readOnly={isReadOnly}
              value={moment(formik.values.birthDate, 'YYYY.MM.DD')}
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'birthDate',
                t('general.birthDate'),
              )}
              onChange={value => {
                formik.setFieldValue('birthDate', moment(value));
              }}
              views={['year', 'month', 'day']}
              format={'YYYY.MM.DD'}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText:
                    formik.touched.birthDate && formik.errors.birthDate,
                  error:
                    formik.touched.birthDate && Boolean(formik.errors.birthDate)
                      ? true
                      : false,
                  name: 'birthDate',
                  InputLabelProps: {
                    shrink: true,
                  },
                },
              }}
            />
          </Grid>
          <Grid xs={12} md={4} item display={'flex'}>
            <FormControl
              disabled={isReadOnly}
              error={formik.touched.gender && Boolean(formik.errors.gender)}
            >
              <FormLabel id="patient-editor-gender-radio-button-group">
                {getLabel(
                  selectedProject?.requiredPatientFields,
                  'gender',
                  t('general.gender'),
                )}
              </FormLabel>

              <RadioGroup
                aria-labelledby="patient-editor-gender-radio-button-group"
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <FormControlLabel
                  disabled={isReadOnly}
                  value={'female'}
                  control={<Radio />}
                  label={t('general.female')}
                />
                <FormControlLabel
                  disabled={isReadOnly}
                  value={'male'}
                  control={<Radio />}
                  label={t('general.male')}
                />
                <FormControlLabel
                  disabled={isReadOnly}
                  value={'other'}
                  control={<Radio />}
                  label={t('general.other')}
                />
              </RadioGroup>
              {formik.touched.gender && Boolean(formik.errors.gender) && (
                <FormHelperText sx={{ ml: 0 }}>
                  {formik.touched.gender && formik.errors.gender}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid xs={12} md={4} item display={'flex'}>
            <FormControl
              disabled={isReadOnly}
              error={formik.touched.isDead && Boolean(formik.errors.isDead)}
            >
              <FormLabel id="patient-editor-deceased-radio-button">
                {getLabel(
                  selectedProject?.requiredPatientFields,
                  'isDead',
                  t('general.deceased'),
                )}
              </FormLabel>
              <RadioGroup
                aria-labelledby="patient-editor-deceased-radio-button"
                value={getIsDeadValue(formik.values.isDead)}
                name="isDead"
                onChange={e => {
                  if (e.target.value === 'yes') {
                    formik.setFieldValue('isDead', true);
                  } else {
                    formik.setFieldValue('deathDate', null);
                    formik.setFieldValue('isDead', false);
                  }
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <FormControlLabel
                  disabled={isReadOnly}
                  value="no"
                  control={<Radio />}
                  label={t('general.no')}
                />
                <FormControlLabel
                  disabled={isReadOnly}
                  value="yes"
                  control={<Radio />}
                  label={t('general.yes')}
                />
              </RadioGroup>
              {formik.touched.isDead && Boolean(formik.errors.isDead) && (
                <FormHelperText sx={{ ml: 0 }}>
                  {formik.touched.isDead && formik.errors.isDead}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {(formik.values.isDead || formik.values.deathDate) && (
            <Grid xs={12} md={12} item>
              <DatePicker
                disableFuture={true}
                readOnly={isReadOnly}
                value={moment(formik.values.deathDate)}
                label={getLabel(
                  selectedProject?.requiredPatientFields,
                  'deathDate',
                  t('projectCreation.availablePatientFields.deathDate'),
                )}
                onChange={value => {
                  formik.setFieldValue('deathDate', moment(value));
                }}
                views={['year', 'month', 'day']}
                format={'YYYY.MM.DD'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText:
                      formik.touched.deathDate && formik.errors.deathDate,
                    error:
                      formik.touched.deathDate &&
                      Boolean(formik.errors.deathDate)
                        ? true
                        : false,
                    name: 'deathDate',
                    InputLabelProps: {
                      shrink: true,
                    },
                  },
                }}
              />
            </Grid>
          )}

          {/* Line 4 */}
          <Grid xs={12} md={2} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'titles',
                t('projectCreation.availablePatientFields.titles'),
              )}
              type="text"
              name="titles"
              value={formik.values.titles}
              onChange={formik.handleChange}
              error={formik.touched.titles && Boolean(formik.errors.titles)}
              helperText={formik.touched.titles && formik.errors.titles}
            />
          </Grid>

          <Grid xs={12} md={5} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'lastName',
                t('general.lastName'),
              )}
              type="text"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Grid>
          <Grid xs={12} md={5} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'firstName',
                t('general.firstName'),
              )}
              type="text"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
          </Grid>

          {/* Line 5 */}

          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'birthLastName',
                t('projectCreation.availablePatientFields.birthLastName'),
              )}
              type="text"
              name="birthLastName"
              value={formik.values.birthLastName}
              onChange={formik.handleChange}
              error={
                formik.touched.birthLastName &&
                Boolean(formik.errors.birthLastName)
              }
              helperText={
                formik.touched.birthLastName && formik.errors.birthLastName
              }
            />
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'birthFirstName',
                t('projectCreation.availablePatientFields.birthFirstName'),
              )}
              type="text"
              name="birthFirstName"
              value={formik.values.birthFirstName}
              onChange={formik.handleChange}
              error={
                formik.touched.birthFirstName &&
                Boolean(formik.errors.birthFirstName)
              }
              helperText={
                formik.touched.birthFirstName && formik.errors.birthFirstName
              }
            />
          </Grid>

          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'motherLastName',
                t('projectCreation.availablePatientFields.motherLastName'),
              )}
              type="text"
              name="motherLastName"
              value={formik.values.motherLastName}
              onChange={formik.handleChange}
              error={
                formik.touched.motherLastName &&
                Boolean(formik.errors.motherLastName)
              }
              helperText={
                formik.touched.motherLastName && formik.errors.motherLastName
              }
            />
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'motherFirstName',
                t('projectCreation.availablePatientFields.motherFirstName'),
              )}
              type="text"
              name="motherFirstName"
              value={formik.values.motherFirstName}
              onChange={formik.handleChange}
              error={
                formik.touched.motherFirstName &&
                Boolean(formik.errors.motherFirstName)
              }
              helperText={
                formik.touched.motherFirstName && formik.errors.motherFirstName
              }
            />
          </Grid>
          {/* Line 6 */}
          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'addressPostcode',
                t('projectCreation.availablePatientFields.addressPostcode'),
              )}
              type="text"
              name="addressPostcode"
              value={formik.values.addressPostcode}
              onChange={formik.handleChange}
              error={
                formik.touched.addressPostcode &&
                Boolean(formik.errors.addressPostcode)
              }
              helperText={
                formik.touched.addressPostcode && formik.errors.addressPostcode
              }
            />
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'addressCity',
                t('projectCreation.availablePatientFields.addressCity'),
              )}
              type="text"
              name="addressCity"
              value={formik.values.addressCity}
              onChange={formik.handleChange}
              error={
                formik.touched.addressCity && Boolean(formik.errors.addressCity)
              }
              helperText={
                formik.touched.addressCity && formik.errors.addressCity
              }
            />
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'addressStreet',
                t('projectCreation.availablePatientFields.addressStreet'),
              )}
              type="text"
              name="addressStreet"
              value={formik.values.addressStreet}
              onChange={formik.handleChange}
              error={
                formik.touched.addressStreet &&
                Boolean(formik.errors.addressStreet)
              }
              helperText={
                formik.touched.addressStreet && formik.errors.addressStreet
              }
            />
          </Grid>
          <Grid xs={12} md={3} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'addressStreet',
                t('projectCreation.availablePatientFields.addressHouseNumber'),
              )}
              type="text"
              name="addressHouseNumber"
              value={formik.values.addressHouseNumber}
              onChange={formik.handleChange}
              error={
                formik.touched.addressHouseNumber &&
                Boolean(formik.errors.addressHouseNumber)
              }
              helperText={
                formik.touched.addressHouseNumber &&
                formik.errors.addressHouseNumber
              }
            />
          </Grid>

          {/* Line 7 */}
          <Grid xs={12} md={6} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              value={formik.values.phoneNumber}
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'phoneNumber',
                t('projectCreation.availablePatientFields.phoneNumber'),
              )}
              onChange={formik.handleChange}
              name="phoneNumber"
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber && formik.errors.phoneNumber
              }
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'email',
                t('general.email'),
              )}
              type="text"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>

          {/* Line 8 */}
          <Grid xs={12} md={12} item>
            <TextField
              InputProps={{
                readOnly: isReadOnly,
              }}
              fullWidth
              label={getLabel(
                selectedProject?.requiredPatientFields,
                'notes',
                t('projectCreation.availablePatientFields.notes'),
              )}
              multiline={true}
              rows={4}
              type="text"
              name="notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              error={formik.touched.notes && Boolean(formik.errors.notes)}
              helperText={formik.touched.notes && formik.errors.notes}
            />
          </Grid>

          <Box display="flex" justifyContent="end">
            {!isReadOnly && <SaveButton isLoading={formik.isSubmitting} />}
            {isReadOnly && (
              <Button
                sx={{ position: 'absolute', top: 15, right: 60, zIndex: 9999 }}
                variant="contained"
                color="warning"
                onClick={() => navigate(-1)}
              >
                {t('general.cancel')}
              </Button>
            )}
          </Box>
        </Grid>
      </form>
    </Paper>
  );
}
